import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { useAuthStore } from '../store/authStore';
import { Check, X } from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  description: string;
  features: Array<{ name: string; included: boolean }>;
  cta: string;
  highlighted: boolean;
}

const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Gratuito',
    price: 0,
    period: 'para sempre',
    description: 'Comece a explorar Soflexo',
    features: [
      { name: '5 perguntas por dia', included: true },
      { name: 'Chat com IA', included: true },
      { name: 'Simulador básico', included: true },
      { name: 'Checklist de documentos', included: true },
      { name: 'Histórico de conversas', included: false },
      { name: 'Alertas de renovação', included: false },
      { name: 'Relatórios PDF', included: false },
      { name: 'Suporte prioritário', included: false },
    ],
    cta: 'Começar Grátis',
    highlighted: false,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 99,
    period: 'por mês',
    description: 'Acesso completo a todas as funcionalidades',
    features: [
      { name: 'Perguntas ilimitadas', included: true },
      { name: 'Chat com IA avançado', included: true },
      { name: 'Simulador completo', included: true },
      { name: 'Checklist de documentos', included: true },
      { name: 'Histórico de conversas', included: true },
      { name: 'Alertas de renovação', included: true },
      { name: 'Relatórios PDF', included: true },
      { name: 'Suporte prioritário', included: true },
    ],
    cta: 'Assinar Agora',
    highlighted: true,
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);

  const handleSelectPlan = async (planId: string) => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (planId === 'free') {
      navigate('/dashboard');
      return;
    }

    setIsLoading(true);
    try {
      // Aqui seria chamada a API para criar sessão Stripe
      alert('Redirecionando para checkout...');
      // await api.createCheckoutSession(planId);
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert('Erro ao processar pagamento');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-poppins font-bold text-gray-900">Soflexo</h1>
          {user && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              Dashboard
            </Button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Title */}
        <div className="text-center mb-16 animate-slide-down">
          <h2 className="text-4xl md:text-5xl font-poppins font-bold text-gray-900 mb-4">
            Planos Simples e Transparentes
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Escolha o plano perfeito para sua empresa
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {PLANS.map((plan) => (
            <Card
              key={plan.id}
              className={`relative transition-all duration-300 ${
                plan.highlighted
                  ? 'md:scale-105 ring-2 ring-blue-600 shadow-2xl'
                  : ''
              } animate-slide-up`}
            >
              {plan.highlighted && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-600 to-cyan-400 text-white px-4 py-1 rounded-bl-lg text-sm font-semibold">
                  Mais Popular
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-2xl font-poppins font-bold text-gray-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-poppins font-bold text-gray-900">
                    R$ {plan.price}
                  </span>
                  <span className="text-gray-600">
                    {plan.price > 0 ? `/${plan.period}` : plan.period}
                  </span>
                </div>
              </div>

              <Button
                variant={plan.highlighted ? 'primary' : 'secondary'}
                size="lg"
                isLoading={isLoading}
                onClick={() => handleSelectPlan(plan.id)}
                className="w-full mb-8"
              >
                {plan.cta}
              </Button>

              {/* Features */}
              <div className="space-y-3">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    {feature.included ? (
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <X className="h-5 w-5 text-gray-300 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        feature.included ? 'text-gray-900' : 'text-gray-400'
                      }`}
                    >
                      {feature.name}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h3 className="text-2xl font-poppins font-bold text-gray-900 mb-8 text-center">
            Perguntas Frequentes
          </h3>
          <div className="space-y-4">
            {[
              {
                q: 'Posso cancelar minha assinatura a qualquer momento?',
                a: 'Sim! Você pode cancelar sua assinatura Premium a qualquer momento sem penalidades.',
              },
              {
                q: 'Qual é a diferença entre Gratuito e Premium?',
                a: 'O plano Gratuito oferece 5 perguntas por dia e funcionalidades básicas. O Premium oferece perguntas ilimitadas, alertas de renovação e relatórios PDF.',
              },
              {
                q: 'Vocês oferecem suporte?',
                a: 'Sim! Usuários Premium têm acesso a suporte prioritário por email.',
              },
              {
                q: 'Posso fazer upgrade depois?',
                a: 'Claro! Você pode fazer upgrade de Gratuito para Premium a qualquer momento.',
              },
            ].map((item, index) => (
              <Card key={index}>
                <h4 className="font-poppins font-bold text-gray-900 mb-2">
                  {item.q}
                </h4>
                <p className="text-gray-600 text-sm">{item.a}</p>
              </Card>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <p className="text-gray-400">
            © 2024 Soflexo. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
